<template>
  <div>
    <div v-if="error" class="tw-text-red-500">
      {{ t('media.error') }}
    </div>
    <div v-if="$q.platform.is.nativeMobile" class="tw-flex tw-justify-center tw-items-center">
      <q-btn
        rounded
        color="primary"
        :disabled="!(canUploadFile && !capturing) || !imagesFinishedLoading"
        @click="capturePhoto"
        class="tw-mr-2"
      >
        {{ t('media.takePhoto') }}
      </q-btn>
      <q-btn
        rounded
        color="red"
        @click="clearPhotos"
        :disabled="numFiles < 1 || !imagesFinishedLoading"
      >
        {{ t('media.clearPhotos') }}
      </q-btn>
    </div>
    <div v-else>
      <q-file
        v-model="desktopUploads"
        outlined
        :label="`${images.length} photo(s) selected`"
        :disable="!canUploadFile || !imagesFinishedLoading"
        :multiple="props.multiple"
        :max-files="props.multiple ? undefined : 1"
        accept=".jpg, image/*"
      >
        <template #prepend>
          <q-icon name="attach_file" />
        </template>
      </q-file>
    </div>
    <div
      v-if="capturing"
      class="tw-mt-3 tw-flex tw-flex-col tw-justify-center tw-items-center tw-text-center"
    >
      <div>{{ t('media.savingPhoto') }}</div>
      <q-spinner color="primary" size="3rem" />
    </div>
    <template v-if="!imagesFinishedLoading">
      <div
        v-for="n in fileCount"
        :key="n"
        class="tw-flex tw-flex-col tw-justify-center tw-items-center"
        style="position: relative; margin-top: 10px"
      >
        <q-skeleton width="300px" height="300px"></q-skeleton>
      </div>
    </template>
    <div
      v-for="image in images"
      :key="`${recapType}-${taskOrStoreId}-${image.id}`"
      class="tw-flex tw-flex-col tw-justify-center tw-items-center"
      style="position: relative; margin-top: 10px"
    >
      <q-img width="300px" height="300px" :src="addDataTag(image.data)" fit="contain"></q-img>
      <q-btn
        style="position: absolute; right: 25px; top: 10px; background-color: rgb(244, 67, 54, 0.7)"
        class="text-white"
        @click="clearPhoto(image)"
        round
        icon="remove"
      ></q-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, toRef } from 'vue'
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera'
import { useI18n } from 'vue-i18n'
import { useBackgroundGeolocation } from '../../../composables/backgroundGeolocation'
import { FileReference, RecapImageUpload, RecapImagesModel } from '../../../models/RecapImagesModel'
import { v4 as uuid4 } from 'uuid'
import { storeToRefs } from 'pinia'
import { useTaskProfiles } from '../../../store/useTaskProfiles'
import { getCurrentStore, logInfo } from '../../..//utils'
import { Capacitor } from '@capacitor/core'
import { useUserStore } from '../../../store/useUserStore'
import { StoreRecapImageUpload, StoreRecapImagesModel } from '../../../models/StoreRecapImagesModel'
import { useCurrentCycle } from '../../../store/useCurrentCycle'
import { Nullish } from '../../../types/utility'
import { useSharedRecap } from '../../../composables/recaps/useRecap'
import { QImg, QSkeleton } from 'quasar'

// Props and emits
const props = withDefaults(
  defineProps<{
    recapFieldInstanceId: string
    recapFieldInstanceRepeatingIndex: number
    fileCount: number
    multiple?: boolean
  }>(),
  {
    recapFieldInstanceRepeatingIndex: 0,
    multiple: false,
  }
)
const emit = defineEmits<{
  (e: 'allFilesRemoved'): void
  (e: 'fileAdded', data: { num: number }): void
}>()

const { recapId, recapType, taskOrStoreId } = useSharedRecap()

const { t } = useI18n({ useScope: 'global' })

// Images
const images = ref<FileReference[]>([])
const fileCount = toRef(props, 'fileCount')
const imagesFinishedLoading = computed(() => !(fileCount.value > 0 && !images.value.length))
const desktopUploads = ref<File | FileList | File[] | null | undefined>()
watch(desktopUploads, async () => {
  await uploadDesktopFiles()
})

onMounted(() => {
  // Defer loading existing images
  setTimeout(async () => {
    let fetchedImages: RecapImageUpload[] | StoreRecapImageUpload[] = []
    if (recapType.value === 'task') {
      const { getRecapImagesForRecapField } = RecapImagesModel()
      fetchedImages = await getRecapImagesForRecapField(
        recapId.value,
        taskOrStoreId.value,
        props.recapFieldInstanceId,
        props.recapFieldInstanceRepeatingIndex
      )
    } else if (recapType.value === 'store') {
      const { getStoreRecapImagesForRecapField } = StoreRecapImagesModel()
      fetchedImages = await getStoreRecapImagesForRecapField(
        recapId.value,
        taskOrStoreId.value,
        props.recapFieldInstanceId,
        props.recapFieldInstanceRepeatingIndex
      )
    }
    for (const image of fetchedImages) {
      images.value.push({
        id: image.path,
        data: image.data,
      })
    }
  })
})

const numFiles = computed(() => {
  return images.value.length
})
watch(numFiles, () => {
  if (numFiles.value === 0) {
    emit('allFilesRemoved')
  } else {
    emit('fileAdded', { num: numFiles.value })
  }
})

const error = ref(false)
const capturing = ref(false)
const canUploadFile = computed(() => !!(props.multiple || numFiles.value === 0))

async function capturePhoto() {
  error.value = false
  capturing.value = true
  if (canUploadFile.value) {
    if (Capacitor.isNativePlatform()) {
      try {
        // Use Capacitor to grab a photo
        const image = await Camera.getPhoto({
          webUseInput: true,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera, // Prevent the photo from being selected from the gallery
          width: 1260,
          quality: 50,
        })
        await savePhoto(image)
      } catch (err) {
        error.value = true
        capturing.value = false
        if ((err as Error).message !== 'User cancelled photos app') {
          logInfo('error', 'PhotoUploaderCapturePhoto', 'Failed to save photo', {
            error: err,
          })
        }
      }
    }
  } else {
    capturing.value = false
    error.value = true
    logInfo(
      'error',
      'PhotoUploaderCapturePhotoProhibited',
      'Tried to capture photo but should not be able to',
      { canUploadFile: canUploadFile.value, numFiles: numFiles.value }
    )
  }
}

function getDesktopFileArray(): File[] {
  if (desktopUploads.value) {
    if (desktopUploads.value instanceof File) {
      return [desktopUploads.value]
    } else if (desktopUploads.value instanceof FileList) {
      // It's not null, undefined or a single file, so it's some sort of iterable object
      return Array.from(desktopUploads.value)
    } else {
      return [...desktopUploads.value]
    }
  }
  return []
}

async function uploadDesktopFiles() {
  if (desktopUploads.value && canUploadFile.value) {
    const files = getDesktopFileArray()
    for (const file of files) {
      await uploadPhotoDesktop(file)
    }
    // Clear the now uploaded files from the input
    desktopUploads.value = null
  }
}

async function uploadPhotoDesktop(file: File) {
  error.value = false
  if (canUploadFile.value) {
    const fileReader = new FileReader()
    fileReader.onloadend = async () => {
      // Save the photo appropriately
      const image = fileReader.result
      await savePhoto(image)
    }
    fileReader.readAsDataURL(file)
  }
}

async function savePhoto(file: string | ArrayBuffer | null | Photo) {
  const fileUUID = uuid4()
  try {
    // Make sure there's a result
    let imageData: string | Nullish = null
    if (isCapacitorPhoto(file)) {
      // The file is a Capacitor Photo object
      imageData = file.base64String
    } else {
      // The file came from the QInput and should already be the data itself
      imageData = file?.toString()
    }
    if (!imageData) {
      throw new Error('Error capturing image. Please try again')
    }
    imageData = addDataTag(imageData)

    // Defer saving the image so we don't block any threads in the meantime
    try {
      // Save the file to the store
      if (recapType.value === 'task') {
        const { writeRecapImage } = RecapImagesModel()
        await writeRecapImage(
          recapId.value,
          taskOrStoreId.value,
          props.recapFieldInstanceId,
          props.recapFieldInstanceRepeatingIndex,
          { path: fileUUID, metadata: await getMetadata() },
          imageData
        )
      } else {
        const { writeStoreRecapImage } = StoreRecapImagesModel()
        await writeStoreRecapImage(
          recapId.value,
          taskOrStoreId.value,
          props.recapFieldInstanceId,
          props.recapFieldInstanceRepeatingIndex,
          { path: fileUUID, metadata: await getMetadata() },
          imageData
        )
      }
      images.value.push({ id: fileUUID, data: imageData! })
      capturing.value = false
    } catch (err) {
      error.value = true
      logInfo('error', 'PhotoUploaderSavePhotoTimeout', 'Failed to save photo', {
        fileUUID,
        imageData,
        error: err,
      })
    } finally {
      capturing.value = false
    }
  } catch (err) {
    error.value = true
    capturing.value = false
    logInfo('error', 'PhotoUploaderSavePhoto', 'Failed to save photo', {
      fileUUID,
      error: err,
    })
  }
}

function isCapacitorPhoto(val: any): val is Photo {
  return typeof val === 'object' && 'base64String' in val
}

function addDataTag(data: string) {
  const hasPrefix = /^data:image\/.*;base64,/.test(data)
  if (!hasPrefix) {
    // Default to JPEG
    // This might not work for every image on desktop, but if we're
    // missing this we don't have a good way to get the image type otherwise as far as I know
    return `data:image/jpeg;base64,${data}`
  }
  return data
}

async function getMetadata() {
  const { getTaskByTaskId } = useTaskProfiles()
  let task = null
  if (recapType.value === 'task') {
    task = getTaskByTaskId(taskOrStoreId.value)
  }
  const store = getCurrentStore()

  const geolocationDataObject: { latitude?: number; longitude?: number } = {}
  if (Capacitor.isNativePlatform()) {
    try {
      const { getGPSPosition } = useBackgroundGeolocation()
      const geolocationData = await getGPSPosition(5000, true)
      geolocationDataObject.latitude = geolocationData?.latitude
      geolocationDataObject.longitude = geolocationData?.longitude
    } catch (error) {
      logInfo('error', 'PhotoUploaderGeocoordinates', 'Failed to get geocoordinates', {
        error,
      })
    }
  }

  const userStore = useUserStore()

  const { getCycle } = storeToRefs(useCurrentCycle())

  const metadata = {
    type: 'recap',
    events_recaps_id: recapId.value,
    rep_id: userStore.id,
    rep_name: userStore.first_name + ' ' + userStore.last_name,
    staffing_users_id: userStore.id,
    captured_app_version: process.env.ANDROID_VERSION_NAME ?? null,
    field_instance_id: props.recapFieldInstanceId,
    repeating_index: props.recapFieldInstanceRepeatingIndex,
    lat: geolocationDataObject?.latitude,
    lng: geolocationDataObject?.longitude,
    task_id: task?.task_id,
    task_type: task?.task_type,
    cycle_number: getCycle.value?.cycle_number,
    job_name: task?.job_name,
    order_line_id: task?.orderline_id,
    space_id: task?.space_id,
    sub_task_id: task?.sub_task_id,
    store_name: store?.store_name,
    store_number: store?.store_number,
    zip: store?.zip,
    street: store?.street,
    city: store?.city,
    state: store?.state,
    store_id: store?.store_id,
    region_id: store?.region_id,
    region: store?.region_name,
    market_id: store?.market_id,
    market: store?.market,
    area_id: store?.area_id,
    area: store?.area,
    territory_id: store?.territory_id,
    territory: store?.territory,
    client_account_id: task?.client_account_id,
    client_account_name: task?.client_account_name,
    client_name: task?.client_name,
    chain_id: store?.chain_id,
    chain_name: store?.chain_name,
    chain_short_name: store?.chain_short_name,
    chain_owner_id: store?.parent_chain_id,
    chain_owner_name: store?.parent_chain_name,
    chain_owner_short_name: store?.parent_short_name,
    product_line: task?.product_line,
    capture_timestamp: new Date().toISOString(),
    artwork_id: task?.artwork_id,
  }
  return metadata
}

async function clearPhotos() {
  error.value = false
  for (const file of images.value) {
    await clearPhoto(file)
  }
  images.value.splice(0, images.value.length)
  desktopUploads.value = null
}

async function clearPhoto(image: FileReference) {
  error.value = false
  // Make sure the file is removed from the device and database
  // Find the index of the removed file in our files list
  let deleted = false
  if (recapType.value === 'task') {
    const { removeRecapImage } = RecapImagesModel()
    deleted = await removeRecapImage(image.id)
  } else {
    const { removeStoreRecapImage } = StoreRecapImagesModel()
    deleted = await removeStoreRecapImage(image.id)
  }
  if (!deleted) {
    logInfo('error', 'PhotoUploaderClearPhoto', 'Failed to remove photo from database or device', {
      image,
    })
    throw new Error('Failed to remove photo from database or device')
  }
  const fileIndex = images.value.findIndex((file) => file.id === image.id)
  if (fileIndex > -1) {
    images.value.splice(fileIndex, 1)
  }
}

defineExpose({
  clearPhotos,
})
</script>
